import React from 'react';
import {Avatar, Box, Button, Chip, IconButton, Paper, Typography} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {DatePicker} from '@mui/x-date-pickers';
import {styled} from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';

const UserCard = ({
                    user,
                    searchTerm,
                    paymentDate,
                    paymentHistories,
                    onDateChange,
                    onUpdatePayment,
                    onClearPayment,
                    highlightMatch
                  }) => {

  const IDBadge = styled('div')({
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: '#1976d2',
    fontWeight: '500',
    border: '1px solid rgba(25, 118, 210, 0.3)',
  });

  // Get sorted payment history (newest first) - Handle Firestore Timestamps
  const sortedPayments = [...(paymentHistories || [])]
    .sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(0);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(0);
      return dateB - dateA;
    });

  // Calculate active payment ranges - Handle Firestore Timestamps
  const activeRanges = sortedPayments
    .filter(p => p.type === "payment")
    .map(p => {
      const start = p.date?.toDate ? p.date.toDate() : new Date(0);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      end.setDate(end.getDate() - 1);
      return {start, end, data: p};
    });

  // Disable dates that would create overlaps
  const shouldDisableDate = (date) => {
    if (!date) return false;
    const testDate = new Date(date);
    const testEnd = new Date(testDate);
    testEnd.setFullYear(testEnd.getFullYear() + 1);

    return activeRanges.some(range =>
      (testDate >= range.start && testDate < range.end) ||
      (testEnd > range.start && testEnd <= range.end) ||
      (testDate <= range.start && testEnd >= range.end)
    );
  };

  // Get current payment status (active) - Handle Firestore Timestamps
  const getPaymentStatus = () => {
    const payments = (paymentHistories || [])
      .filter(p => p.type === "payment")
      .sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(0);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(0);
        return dateB - dateA;
      });

    if (payments.length === 0) return {status: 'unpaid'};

    const now = new Date();
    const paymentDateObj = payments[0].date?.toDate ? payments[0].date.toDate() : new Date(0);
    const expiryDate = new Date(paymentDateObj);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    expiryDate.setDate(expiryDate.getDate() - 1);

    if (now < paymentDateObj) {
      return {status: 'future', paymentDate: paymentDateObj, expiryDate};
    } else if (now <= expiryDate) {
      return {status: 'active', paymentDate: paymentDateObj, expiryDate};
    } else {
      return {status: 'unpaid'};
    }
  };

  const paymentStatus = getPaymentStatus();

  return (
    <Paper elevation={3} sx={{
      p: 2,
      position: 'relative',
      backgroundColor: paymentStatus.status === 'active' ? "#e5ffe5" :
        paymentStatus.status === 'future' ? "#fff9e5" : "#ffe5e5",
      pb: 6
    }}>
      {/* Club Header with Image */}
      <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem'}}>
        <Avatar
          src={user.clubImage || ''}
          sx={{width: 56, height: 56}}
        >
          {user.nameShort?.[0] || user.name?.[0] || user.email?.[0]}
        </Avatar>
        <div style={{marginLeft: '1rem'}}>
          <Typography
            variant="subtitle1"
            sx={{fontWeight: 'bold'}}
            dangerouslySetInnerHTML={{
              __html: highlightMatch(user.name || 'Unnamed Club', searchTerm)
            }}
          />
          {user.nameShort && (
            <Typography variant="caption" color="text.secondary">
              <span dangerouslySetInnerHTML={{
                __html: highlightMatch(user.nameShort, searchTerm)
              }}/>
            </Typography>
          )}
        </div>
      </div>

      {/* Club Details */}
      {user.style && (
        <Typography variant="body2">
          <strong>Style:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch(user.style, searchTerm)
        }}/>
        </Typography>
      )}

      {user.contact && (
        <Typography variant="body2">
          <strong>Contact:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch(user.contact, searchTerm)
        }}/>
        </Typography>
      )}

      {user.email && (
        <Typography variant="body2">
          <strong>Email:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch(user.email, searchTerm)
        }}/>
        </Typography>
      )}

      {user.telephone && (
        <Typography variant="body2">
          <strong>Phone:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch(user.telephone, searchTerm)
        }}/>
        </Typography>
      )}

      {(user.address1 || user.address2) && (
        <Typography variant="body2">
          <strong>Address:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch([user.address1, user.address2].filter(Boolean).join(', '), searchTerm)
        }}/>
        </Typography>
      )}

      {user.country && (
        <Typography variant="body2">
          <strong>Country:</strong> <span dangerouslySetInnerHTML={{
          __html: highlightMatch(user.country, searchTerm)
        }}/>
        </Typography>
      )}

      {/* Payment Status */}
      <div style={{marginBottom: '1rem'}}>
        {paymentStatus.status === 'active' && (
          <>
            <Chip
              label={`Paid: ${paymentStatus.paymentDate.toLocaleDateString()}`}
              color="success"
              sx={{fontWeight: 'bold'}}
            />
            <Chip
              label={`Expires: ${paymentStatus.expiryDate.toLocaleDateString()}`}
              color="default"
              sx={{ml: 1}}
            />
          </>
        )}
        {paymentStatus.status === 'future' && (
          <>
            <Chip
              label={`Future: ${paymentStatus.paymentDate.toLocaleDateString()}`}
              color="warning"
              sx={{fontWeight: 'bold'}}
            />
            <Chip
              label={`Will expire: ${paymentStatus.expiryDate.toLocaleDateString()}`}
              color="default"
              sx={{ml: 1}}
            />
          </>
        )}
        {(paymentStatus.status === 'unpaid') && (
          <Chip
            label="Unpaid"
            color="error"
            sx={{fontWeight: 'bold'}}
          />
        )}
      </div>

      {/* Date Picker */}
      <DatePicker
        label="Payment Date"
        value={paymentDate}
        onChange={onDateChange}
        shouldDisableDate={shouldDisableDate}
        slotProps={{
          textField: {
            error: paymentDate && shouldDisableDate(paymentDate),
            helperText: paymentDate && shouldDisableDate(paymentDate)
              ? "Overlaps with existing payment"
              : "Select any non-overlapping date"
          }
        }}
      />

      <Button
        variant="contained"
        onClick={onUpdatePayment}
        fullWidth
        sx={{mt: 1}}
      >
        Save Payment
      </Button>

      {paymentHistories?.length > 0 ? (
        <Box sx={{mt: 2, borderTop: '1px dashed #ddd', pt: 1}}>
          <Typography variant="subtitle2" gutterBottom>
            Payment History
          </Typography>
          <Box sx={{maxHeight: 100, overflowY: 'auto'}}>
            {sortedPayments.map((payment, i) => {
              const paymentDateObj = payment.date?.toDate ? payment.date.toDate() : null;
              const expiryDate = paymentDateObj ? new Date(paymentDateObj) : null;
              if (expiryDate) expiryDate.setFullYear(expiryDate.getFullYear() + 1);

              return (
                <Box key={i} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5}}>
                  <span>
                    {paymentDateObj ? paymentDateObj.toLocaleDateString() : 'Cleared'}
                    {expiryDate && ` → ${expiryDate.toLocaleDateString()}`}
                  </span>
                  <IconButton
                    size="small"
                    onClick={() => onClearPayment(payment.id)}
                  >
                    <ClearIcon fontSize="small"/>
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box sx={{mt: 2, borderTop: '1px dashed #ddd', pt: 1}}>
          <Typography variant="subtitle2" color="text.secondary">
            No payment history yet
          </Typography>
        </Box>
      )}

      {user.id && (
        <Tooltip title="Copy ID">
          <IDBadge>
            ID: {user.id}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(user.id);
              }}
              sx={{
                p: 0,
                ml: 0.5,
                color: 'inherit',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </IDBadge>
        </Tooltip>
      )}

    </Paper>
  );
};

export default React.memo(UserCard);